/**
 * Покрытия комнат переживают новую стену: пол, гидроизоляция, стяжка, потолок и название.
 *
 * Покрытие хранится по id комнаты, а id комнаты — это её углы (`roomIdOf`). Стена,
 * примкнувшая к наружной стене, режет её новым узлом — у соседней комнаты на контуре
 * появляется ещё один угол, id меняется, и пол «пропадал». Здесь после постройки стены
 * каждая новая комната без покрытия получает покрытие той прежней комнаты, внутри которой
 * она оказалась: комната с лишним углом — своё же, разрезанная стеной пополам — обе
 * половины тот же пол. Покрытия комнат, которых больше нет, убираем.
 */
import { pointInPolygon, type Pt } from "@/shared/lib/geometry";
import { AnalyticsGoal, trackGoal } from "@/shared/lib/analytics";
import { effectiveWallGraph } from "@/shared/plan";
import { addWall } from "@/shared/store/actions/addWallAction";
import { type NewWallOptions, useProjectStore } from "@/shared/store";
import {
	useCeilingStore,
	useFloorsStore,
	useRoomNamesStore,
	useScreedStore,
	useWallsStore,
	useWaterproofStore,
} from "@/shared/store/layers";
import { detectRooms, type Room } from "@/widgets/plan";

/** Покрытие комнаты: у всех слоёв оно хранится по id комнаты и знает его. */
interface RoomKeyed {
	id: string;
	roomId: string;
}

/**
 * Переносит покрытия одного слоя на новые комнаты. Возвращает новые записи или null,
 * если ничего не поменялось.
 */
const carry = <T extends RoomKeyed>(
	items: Record<string, T>,
	before: Room[],
	after: Room[],
): Record<string, T> | null => {
	const alive = new Set(after.map((room) => room.id));
	let changed = false;
	const next: Record<string, T> = {};
	for (const [id, item] of Object.entries(items)) {
		if (alive.has(id)) next[id] = item;
		else changed = true;
	}
	for (const room of after) {
		if (next[room.id]) continue;
		const source = before.find(
			(old) => old.id !== room.id && items[old.id] && pointInPolygon(room.label, old.inner),
		);
		if (!source) continue;
		next[room.id] = { ...items[source.id], id: room.id, roomId: room.id };
		changed = true;
	}
	return changed ? next : null;
};

/** Перенести покрытия всех слоёв с комнат `before` на нынешние комнаты чертежа. */
export const keepRoomCoverings = (before: Room[]) => {
	const after = detectRooms(effectiveWallGraph(useProjectStore.getState().project.layers.walls));
	const floors = carry(useFloorsStore.getState().items, before, after);
	if (floors) useFloorsStore.getState().restoreFloors(floors);
	const waterproof = carry(useWaterproofStore.getState().items, before, after);
	if (waterproof) useWaterproofStore.getState().restoreWaterproofs(waterproof);
	const screed = carry(useScreedStore.getState().items, before, after);
	if (screed) useScreedStore.getState().restoreScreed(screed);
	const ceilings = carry(useCeilingStore.getState().items, before, after);
	if (ceilings) useCeilingStore.getState().restoreCeilings(ceilings);
	const names = carry(useRoomNamesStore.getState().items, before, after);
	if (names) useRoomNamesStore.getState().restoreRoomNames(names);
};

/** Построить стену, сохранив покрытия комнат, которые она задела. */
export const addWallKeepingRooms = (
	from: Pt,
	to: Pt,
	options: NewWallOptions | undefined,
): string | null => {
	const before = detectRooms(effectiveWallGraph(useProjectStore.getState().project.layers.walls));
	const wasEmpty = Object.keys(useWallsStore.getState().items.walls).length === 0;
	const endNodeId = addWall(from, to, options);
	keepRoomCoverings(before);
	if (wasEmpty && Object.keys(useWallsStore.getState().items.walls).length > 0) {
		trackGoal(AnalyticsGoal.FirstWallCreated);
	}
	return endNodeId;
};
